import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Skeleton } from "@mui/material";
import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { useSettingContext } from "context/SettingContext";
import RemoveModal from "components/ui/RemoveModal/RemoveModal";
import { getIcon } from "utils/iconsConfig";
import ClientModal from "components/ui/clientModal/clientModal";
import { useGlobalContext } from "context/GlobalContext";
import { formatTrayName } from "utils/capitalEachLetter";
import { useEventType } from "./Api/eventTypeApi";
import apiClient from "lib/api-client";
import { findSpecificMenu, findSpecificSubMenu, isAllowed } from "utils/checkUserRigths";
import { usePermissions } from "context/PermissionsContext";

const StyledTableContainer = styled(TableContainer)(({ }) => ({
  backgroundColor: "transparent",
  maxHeight: "72.5vh",
  // height: "70vh",
  overflowX: "auto",
  position: "relative",
  border: "1px solid #fff",
  background: "#000",
  scrollbarWidth: "none", // Firefox
  "&::-webkit-scrollbar": {
    height: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "3px",
  },

  "@media (max-width: 768px)": {
    maxWidth: "100%",
    overflowX: "scroll",
  },
}));

const StyledTable = styled(Table)(({ }) => ({
  "& thead": {
    backgroundColor: "#444444",
    position: "sticky",
    top: 0,
    zIndex: 1,
    "& th": {
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "22px",
      color: "#FFFFFF",
      textAlign: "center",
      fontFamily: "Poppins",
      padding: "10px",
      borderBottom: "1px solid #fff",
    },
  },
  "& tbody": {
    "& td": {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "22px",
      color: "#FFFFFF",
      textAlign: "center",
      fontFamily: "Poppins",
      borderBottom: "1px solid #fff",
      padding: "10px 5px",
      border: "1px solid #fff",
    },
  },
  "& th": {
    // backgroundColor: "#444444",
    height: "50px",
    // position: "sticky",
    // top: 0,
    // zIndex: 1,
  },
}));

const StyledTableHeaderCell = styled(TableCell)`
  padding: 15px;
  font-size: 14px !important;
  font-weight: 400 !important;
  background-color: #444444;
  color: white;
  text-align: center !important;
  position: sticky;
  top: 0;
  z-index: 10;
  border: 1px solid #fff;

  &.narrow {
    width: 20%;
  }

  &.default {
    width: 40%;
  }
`;

const StyledTableRow = styled(TableRow)`
  border-bottom: 1px solid #fff;
  & td {
    padding: 5px;
  }
`;

const StyledTableBodyCell = styled(TableCell)`
  padding: 10px;
  text-align: center;

   &.narrow {
    width: 20%; 

  &.default {
    width: 40%;
  }
`;

const buttonStyle = (color) => ({
  backgroundColor: "transparent",
  color,
  padding: "3px 12px",
  border: `1px solid ${color}`,
  borderRadius: "6px",
  fontWeight: 500,
  fontFamily: "Poppins",
  fontSize: "14px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "5px",
});

function debounce(fn, delay) {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

const EventType = () => {
  const { setCreateBtn, setCreateBtnName, addClient, setAddClient, search, setSearch, setSearchBar, editTrayData, setEditTrayData } = useSettingContext();
  const { setSearchPlaceHolder } = useGlobalContext();
  const [dustbinModalOpen, setDustbinModalOpen] = useState(false);
  const [selectedEventName, setSelectedEventName] = useState(null);
  const tableRef = useRef(null);

  useEffect(() => {
    setSearchBar(true);
    setSearchPlaceHolder("Search By Event name");

    return () => {
      setSearchBar(false);
      setSearch("");
      setSearchPlaceHolder("");
    };
  }, []);

  // ----------------------------------------------------  GET ALL EVENT TYPE  --------------------------------------------------------------------------

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, isError, refetch } = useEventType({
    limit: 15,
    search,
  });

 const allEventData = data?.pages?.flatMap((page) => page.eventTypeData) ?.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)) || [];

  const handleScroll = debounce(() => {
    const element = tableRef.current;
    if (!element || isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = element;
    const reachedBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (reachedBottom) {
      fetchNextPage();
    }
  }, 300);

  useEffect(() => {
    setCreateBtn(true);
    setCreateBtnName("Add Event +");
    return () => {
      setCreateBtn(false);
      setCreateBtnName("");
    };
  }, []);

  const discardChanges = () => {
    setDustbinModalOpen(false);
  };

  const permission = usePermissions()
  const hasAccessToEditButton = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Event Type"), "edit");
  const hasAccessToDelete = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Event Type"), "delete");

  const handleDelete = (rowData) => {
    setSelectedEventName(rowData);
    setDustbinModalOpen(true);
  };

  // ----------------------------------------------------  DELETE EVENT TYPE  --------------------------------------------------------------------------

  const handleConfirmDelete = async () => {
    try {
      const response = await apiClient.put(`/api/v1/vision_events/delete/${selectedEventName?._id}`);

      if (response.status === 200) {
        discardChanges();
        refetch?.();
      } else {
        console.error(response.data?.message || "Failed to delete eventType.");
      }
    } catch (error) {
      console.error("Error deleting eventType:", error);
      console.error(error.response?.data?.message || "Something went wrong.");
    }
  };

  const btnInfo = [
    {
      btnName: "No",
      bgColor: "#E77B7B",
      click: () => {
        discardChanges();
      },
    },
    {
      btnName: "Yes",
      bgColor: "#50c878",
      click: handleConfirmDelete,
    },
  ];

  const TableSkeleton = ({ columns, rows = 5 }) => (
    <>
      {Array.from(new Array(rows)).map((_, rowIndex) => (
        <StyledTableRow key={`skeleton-${rowIndex}`}>
          {Array.from(new Array(columns)).map((_, cellIndex) => (
            <StyledTableBodyCell key={`skeleton-cell-${cellIndex}`}>
              <Skeleton variant="text" sx={{ fontSize: "1rem" }} animation="wave" width={"100%"} height={"90%"} />
            </StyledTableBodyCell>
          ))}
        </StyledTableRow>
      ))}
    </>
  );

  return (
    <>
      <div style={{ marginTop: "15px", marginLeft: "6px", marginRight: "11.5px", backgroundColor: "#000", padding: "10px" }}>
        <StyledTableContainer ref={tableRef} onScroll={handleScroll}>
          {/* <StyledTableContainer> */}
          <StyledTable>
            <TableHead>
              <TableRow>
                <StyledTableHeaderCell className="narrow">Sr no.</StyledTableHeaderCell>
                <StyledTableHeaderCell className="default">Events</StyledTableHeaderCell>
                <StyledTableHeaderCell className="default">Action</StyledTableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableSkeleton columns={3} rows={10} />
              ) : allEventData?.length === 0 ? (
                <StyledTableRow>
                  <StyledTableBodyCell colSpan={6} style={{ textAlign: "center", padding: "20px", color: "#fff" }}>
                    No Data Available
                  </StyledTableBodyCell>
                </StyledTableRow>
              ) : (
                allEventData.map((item, index) => (
                  <StyledTableRow key={item?._id}>
                    <StyledTableBodyCell className="narrow">{index + 1}.</StyledTableBodyCell>
                    <StyledTableBodyCell className="default">{item?.eventTypeName}</StyledTableBodyCell>
                    <StyledTableBodyCell className="default">
                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                        {
                          hasAccessToEditButton || hasAccessToDelete ? (
                            <>
                              {hasAccessToEditButton &&
                                <button
                                  onClick={() => {
                                    setEditTrayData(item);
                                    setAddClient(true);
                                  }}
                                  style={buttonStyle("#50C878")}
                                >
                                  {getIcon("greenEditIcon")} Edit
                                </button>
                        }
                              {hasAccessToDelete &&
                                <button onClick={() => handleDelete(item)} style={buttonStyle("#E77B7B")}>
                                  {getIcon("DustbinIcon")} Delete
                                </button>
                              }
                            </>
                          ) : (
                            <span>No actions available</span>
                          )}
                      </div>

                    </StyledTableBodyCell>
                  </StyledTableRow>
                ))
              )}
              {isFetchingNextPage && <TableSkeleton columns={3} rows={5} />}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      </div>
      <RemoveModal open={dustbinModalOpen} setOpen={setDustbinModalOpen} btnInfo={btnInfo} heading={`Are you sure you want to delete ${selectedEventName?.eventTypeName}?`} oneLiner={true} />
      <ClientModal
        open={addClient}
        onClose={() => {
          setAddClient(false);
          setEditTrayData(null);
        }}
        type="addEvent"
        editTrayData={editTrayData}
        onSuccess={refetch}
      />
    </>
  );
};

export default EventType;
